import type { ChangeEvent, ClipboardEventHandler, KeyboardEventHandler, RefObject } from "react";
import { useEffect, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Checkbox } from "@similarweb/ui-components";

import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { translate } from "app/filters/i18nFilter";

import { generateAutomationName } from "../../../../../../helpers/generateAutomationName";
import { keywordModalTexts } from "../texts";

import * as SC from "./styles";

type InputSchema = {
    selectedKeywordsCount: number;
    keywordsInputRef: RefObject<HTMLInputElement | null>;
    queryKeyword: string;
    selectedText: string;
    isIncludedInList: boolean;
    keywordsCount: number;
    savedKeywords: any;
    onRemove: (index: number, removeAllSelected: boolean) => void;
    clearSelection: () => void;
    selectKWFromSearch: () => void;
    onAdd: () => void;
    onKeyPress: (event: KeyboardEvent) => void;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onPaste: (event: ClipboardEvent) => void;
    clearQuery: () => void;
};

const Input = ({
    selectedKeywordsCount,
    keywordsInputRef,
    queryKeyword,
    selectedText,
    isIncludedInList,
    keywordsCount,
    savedKeywords,
    onRemove,
    clearSelection,
    selectKWFromSearch,
    onAdd,
    onKeyPress,
    onChange,
    onPaste,
    clearQuery,
}: InputSchema) => {
    const keywordsLimit = ActivationComponentAssets.maxAmountOfKeywords;

    const [isSelected, setIsSelected] = useState(false);
    const isLimitReached = savedKeywords.length >= keywordsLimit;

    const onSelect = () => {
        if (isSelected) {
            clearSelection();
            setIsSelected(false);

            return;
        }

        selectKWFromSearch();
        setIsSelected(true);
    };

    const onRemoveSelected = () => onRemove(0, true);

    useEffect(() => {
        if (isSelected && !selectedKeywordsCount) {
            setIsSelected(false);
        }
    }, [selectedKeywordsCount]);

    return (
        <>
            <SC.InputContainer
                data-automation={generateAutomationName("home", [
                    "topics",
                    "modal",
                    "advanced",
                    "add",
                    "input",
                    "container",
                ])}
            >
                {selectedKeywordsCount ? (
                    <SC.SelectedItemsContainer
                        data-automation={generateAutomationName("home", [
                            "topics",
                            "modal",
                            "advanced",
                            "selected",
                            "menu",
                            "container",
                        ])}
                    >
                        <SC.TextButtonWrapper>
                            <SC.SelectedItemsText>
                                {selectedKeywordsCount} {selectedText}
                            </SC.SelectedItemsText>
                            <IconButton
                                type="invertedTransparent"
                                iconName="delete"
                                onClick={onRemoveSelected}
                            >
                                {translate(keywordModalTexts.deleteButton)}
                            </IconButton>
                        </SC.TextButtonWrapper>

                        <SC.StyledButton
                            type="flat"
                            label={translate(keywordModalTexts.clearSelection)}
                            onClick={clearSelection}
                        />
                    </SC.SelectedItemsContainer>
                ) : (
                    <SC.Input
                        data-automation={generateAutomationName("home", [
                            "topics",
                            "modal",
                            "advanced",
                            "modal",
                            "input",
                        ])}
                        ref={keywordsInputRef}
                        placeholder={translate(keywordModalTexts.keywordInputPlaceholder)}
                        onKeyDown={onKeyPress as unknown as KeyboardEventHandler<HTMLInputElement>}
                        onChange={onChange}
                        onPaste={onPaste as unknown as ClipboardEventHandler<HTMLInputElement>}
                        value={queryKeyword}
                    />
                )}
            </SC.InputContainer>
            {queryKeyword ? (
                <>
                    {isIncludedInList ? (
                        <SC.AddPlaceholder
                            isIncluded
                            data-automation={generateAutomationName("home", [
                                "topics",
                                "modal",
                                "advanced",
                                "selected",
                                "menu",
                                "included",
                            ])}
                        >
                            <SWReactIcons
                                iconName="checked"
                                size="xs"
                                iconColor={colorsPalettes.blue[400]}
                            />
                            <SC.KeywordName>{translate(keywordModalTexts.alreadyIncluded)}</SC.KeywordName>
                        </SC.AddPlaceholder>
                    ) : (
                        <SC.AddPlaceholder
                            isLimitReached={isLimitReached}
                            isIncluded={false}
                            onClick={isLimitReached ? clearQuery : onAdd}
                            data-automation={generateAutomationName("home", [
                                "topics",
                                "modal",
                                "advanced",
                                "selected",
                                "menu",
                                "not",
                                "included",
                            ])}
                        >
                            <SWReactIcons
                                iconName={isLimitReached ? "alert-circle" : "plus-circle-filled"}
                                size="xs"
                                iconColor={isLimitReached ? colorsPalettes.red[400] : colorsPalettes.blue[400]}
                            />
                            {isLimitReached ? (
                                <SC.ErrorTextWrapper>
                                    <SC.ErrorText>{translate(keywordModalTexts.errorLimitText)}</SC.ErrorText>
                                    <SC.ClickText>{translate(keywordModalTexts.clickToClearInput)}</SC.ClickText>
                                </SC.ErrorTextWrapper>
                            ) : (
                                <>
                                    <SC.KeywordName>{queryKeyword}</SC.KeywordName>
                                    <SC.ClickText>{translate(keywordModalTexts.clickToAdd)}</SC.ClickText>
                                </>
                            )}
                        </SC.AddPlaceholder>
                    )}
                    <SC.MatchingKWWrapper
                        data-automation={generateAutomationName("home", [
                            "topics",
                            "modal",
                            "advanced",
                            "modal",
                            "match",
                            "state",
                        ])}
                    >
                        <SC.MatchingKWTitle>{translate(keywordModalTexts.matchingKWTitle)}</SC.MatchingKWTitle>

                        <SC.CheckBoxWrapper>
                            <Checkbox
                                onClick={onSelect}
                                selected={isSelected}
                            />
                            <SC.MatchingKWCountText>
                                {keywordsCount} {translate(keywordModalTexts.alreadyIncludedCount)}
                            </SC.MatchingKWCountText>
                        </SC.CheckBoxWrapper>
                        <SC.DividerWrapper>
                            <SC.Divider />
                        </SC.DividerWrapper>
                    </SC.MatchingKWWrapper>
                </>
            ) : null}
        </>
    );
};

export default Input;
